<script setup lang="ts">
/* eslint-disable vue/no-undef-components */
import { Amplitude } from '@seede/amplitude';
import { computed, toRef } from 'vue';
import { i18n, t } from '~/i18n';
import {
  absoluteLocalizedUrl,
  buildPathHreflangLinks,
  ogLocaleFor,
  siteLocaleFromRouteParam,
} from '@/lib/site-locale';
const locale = toRef(i18n.options, 'locale');
const route = useRoute();
const siteLocale = computed(() => siteLocaleFromRouteParam(route.params.locale));
const canonicalUrl = computed(() => absoluteLocalizedUrl(siteLocale.value, '/'));

const requestURL = useRequestURL();
const ogImageUrl = computed(() => `${requestURL.origin}/Veeso_og.jpg`);

useHead(() => ({
  title: t('landing_home.seo.title'),
  htmlAttrs: { lang: siteLocale.value },
  meta: [
    { name: 'description', content: t('landing_home.seo.description') },
    { name: 'keywords', content: t('landing_home.seo.keywords') },
    { property: 'og:title', content: t('landing_home.seo.title') },
    { property: 'og:type', content: 'website' },
    { property: 'og:url', content: canonicalUrl.value },
    { property: 'og:locale', content: ogLocaleFor(siteLocale.value) },
    { property: 'og:description', content: t('landing_home.seo.description') },
    { property: 'og:image', content: ogImageUrl.value },
    { property: 'og:image:width', content: '1200' },
    { property: 'og:image:height', content: '630' },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:site', content: '@veesoai' },
    { name: 'twitter:title', content: t('landing_home.seo.title') },
    { name: 'twitter:description', content: t('landing_home.seo.description') },
    { name: 'twitter:image', content: ogImageUrl.value },
  ],
  link: [{ rel: 'canonical', href: canonicalUrl.value }, ...buildPathHreflangLinks('/')],
}));

const inviteCodeCookie = useCookie('invite_code', {
  maxAge: 60 * 60 * 24 * 7, // 7 days
  path: '/',
});

const utmCookies = {
  source: useCookie('utm_source', { maxAge: 60 * 60 * 24 * 30, path: '/' }),
  medium: useCookie('utm_medium', { maxAge: 60 * 60 * 24 * 30, path: '/' }),
  campaign: useCookie('utm_campaign', { maxAge: 60 * 60 * 24 * 30, path: '/' }),
  term: useCookie('utm_term', { maxAge: 60 * 60 * 24 * 30, path: '/' }),
  content: useCookie('utm_content', { maxAge: 60 * 60 * 24 * 30, path: '/' }),
};

const scrollDepthMilestones = [25, 50, 75, 100] as const;
const reachedDepths = new Set<number>();

function reportScrollDepth() {
  const doc = document.documentElement;
  const scrollTop = window.scrollY || doc.scrollTop || 0;
  const scrollableHeight = doc.scrollHeight - window.innerHeight;
  const progress = scrollableHeight > 0 ? scrollTop / scrollableHeight : 0;
  const depthPercent = Math.min(100, Math.round(progress * 100));

  for (const milestone of scrollDepthMilestones) {
    if (!reachedDepths.has(milestone) && depthPercent >= milestone) {
      reachedDepths.add(milestone);
      Amplitude.event('scroll_depth_reached', {
        depth_percent: milestone,
        page_path: window.location.pathname,
      });
    }
  }
}

onMounted(() => {
  Amplitude.pageView({
    page_name: 'landing_home',
  });

  window.addEventListener('scroll', reportScrollDepth, { passive: true });
  reportScrollDepth();

  const route = useRoute();
  const { ref, utm_source, utm_medium, utm_campaign, utm_term, utm_content } = route.query;

  if (ref) {
    const refCode = (Array.isArray(ref) ? ref[0] : ref) as string;
    inviteCodeCookie.value = refCode;
    Amplitude.event('referral_visit', {
      ref_code: refCode,
    });
  }

  const getQueryVal = (val: any) => {
    if (Array.isArray(val)) return val[0];
    return val;
  };

  if (utm_source) utmCookies.source.value = getQueryVal(utm_source);
  if (utm_medium) utmCookies.medium.value = getQueryVal(utm_medium);
  if (utm_campaign) utmCookies.campaign.value = getQueryVal(utm_campaign);
  if (utm_term) utmCookies.term.value = getQueryVal(utm_term);
  if (utm_content) utmCookies.content.value = getQueryVal(utm_content);

  if (ref || utm_source || utm_medium || utm_campaign || utm_term || utm_content) {
    const scheduleLowPriority = (fn: () => void) => {
      const w = window as any;
      if (typeof w.requestIdleCallback === 'function') {
        w.requestIdleCallback(fn, { timeout: 3000 });
        return;
      }
      setTimeout(fn, 1200);
    };

    scheduleLowPriority(async () => {
      try {
        const { getApiBaseUrl } = await import('@/composables/useApi');
        const apiBase = getApiBaseUrl();
        $fetch(`${apiBase}/api/visit`, {
          method: 'POST',
          body: {
            ref: getQueryVal(ref),
            url: window.location.href,
            utm_source: getQueryVal(utm_source),
            utm_medium: getQueryVal(utm_medium),
            utm_campaign: getQueryVal(utm_campaign),
            utm_term: getQueryVal(utm_term),
            utm_content: getQueryVal(utm_content),
            metadata: {
              referrer: document.referrer,
              screen_width: window.innerWidth,
              screen_height: window.innerHeight,
            },
          },
        }).catch(() => {});
      } catch (e) {
        console.error(e);
      }
    });
  }
});

onBeforeUnmount(() => {
  window.removeEventListener('scroll', reportScrollDepth);
});
</script>

<template>
  <div class="landing-page min-h-screen relative flex flex-col index-bg" :data-locale="locale">
    <!-- Header -->
    <AppNav active-path="/" />

    <!-- Hero Section -->
    <FeaturesHero />

    <!-- Every Scene Section -->
    <LazyFeaturesEveryScene />

    <!-- Every Step Section -->
    <LazyFeaturesEveryStep />

    <!-- Testimonials Section -->
    <LazyFeaturesTestimonials />

    <!-- FAQ Section -->
    <LazyFeaturesFaq />

    <!-- Roles Section -->
    <LazyFeaturesRoles />

    <!-- Footer -->
    <LazyAppFooter />
  </div>
</template>

<style scoped>
.index-bg {
  position: relative;
}

.index-bg::before {
  content: '';
  position: fixed;
  inset: 0;
  z-index: 0;
  pointer-events: none;
}

.index-bg > :not(header) {
  position: relative;
  z-index: 1;
}
</style>
